import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Modal,
  Typography,
} from "@mui/material";
import { Footer } from "../components/Footer";

export function BankingDetails({
  handleNext,
  handleBack,
  handleSkip,
  activeStep,
  isStepOptional,
  isStepSkipped,
  totalFormData,
  steps,
  handleDataSubmitted,
}: {
  handleNext: () => void;
  handleBack: () => void;
  handleSkip: () => void;
  handleDataSubmitted: (data: Record<string, string>, step: string) => void;
  activeStep: number;
  totalFormData: Record<string, any>;
  isStepOptional: (step: number) => boolean;
  isStepSkipped: (step: number) => boolean;
  steps: string[];
}) {
  const [bank, setBank] = useState("");
  const [accountType, setAccountType] = useState("");
  const [refundAccountOption, setRefundAccountOption] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (totalFormData["banking-details"]) {
      const prev = totalFormData["banking-details"];
      setFormValues(prev);
      setBank(prev.bank || "");
      setAccountType(prev.accountType || "");
      setRefundAccountOption(prev.refundAccountOption || "");
    }
  }, [totalFormData]);

  const validateForm = (values: Record<string, string>) => {
    const errors: Record<string, string> = {};

    if (!values.refundAccountOption) {
      errors.refundAccountOption =
        "Please select where we should refund your claim.";
    }

    // Only validate detailed bank fields when user chooses to enter other bank details
    if (values.refundAccountOption === "Other bank details") {
      if (!values.name) {
        errors.name = "Name of account holder is required";
      }
      if (!values.accountNumber) {
        errors.accountNumber = "Account number is required";
      }
      if (!values.bank) {
        errors.bank = "Bank is required";
      }
      if (!values.accountType) {
        errors.accountType = "Account type is required";
      }
    }

    return errors;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Build values explicitly so that all fields are always present in the payload
    const values: Record<string, string> = {
      refundAccountOption,
      name: formValues.name || "",
      accountNumber: formValues.accountNumber || "",
      bank: formValues.bank || "",
      otherBank: formValues.otherBank || "",
      accountType: formValues.accountType || "",
      otherAccountType: formValues.otherAccountType || "",
    };

    const errors = validateForm(values);

    if (Object.keys(errors).length === 0) {
      console.log("Form values:", values);
      handleDataSubmitted(values, "banking-details");
      handleNext();
    } else {
      setFormErrors(errors);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    handleSkip();
  };

  return (
    <>
      <h4 style={{ borderBottom: "1px solid #ccc", margin: "1em 0" }}>
        Banking details for refunds
      </h4>
      <form onSubmit={handleSubmit} style={{ paddingBottom: "5em" }}>
        <FormControl
          sx={{
            m: { xs: 0, sm: 1 },
            mb: { xs: 1, sm: 1 },
            width: { xs: 300, sm: 600 },
            minWidth: { xs: "100%", sm: "auto" },
          }}
          error={!!formErrors.refundAccountOption}
        >
          <InputLabel id="refund-account-option-label">
            Refund account
          </InputLabel>
          <Select
            labelId="refund-account-option-label"
            id="refund-account-option"
            value={refundAccountOption}
            label="Refund account"
            onChange={(e) => {
              const value = e.target.value as string;
              setRefundAccountOption(value);
              setFormValues({
                ...formValues,
                refundAccountOption: value,
              });
            }}
          >
            <MenuItem value="Bank account being debited">
              Bank account being debited
            </MenuItem>
            <MenuItem value="Other bank details">Other bank details</MenuItem>
          </Select>
          {formErrors.refundAccountOption && (
            <Typography variant="body2" color="error">
              {formErrors.refundAccountOption}
            </Typography>
          )}
        </FormControl>

        {refundAccountOption === "Bank account being debited" && (
          <>
            <Typography
              variant="body2"
              sx={{ m: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 1 }, maxWidth: 600 }}
            >
              We’ll refund to the bank account used for your policy premiums.
            </Typography>
          </>
        )}
        {refundAccountOption === "Other bank details" && (
          <>
            <Typography
              variant="body2"
              sx={{ m: { xs: 0, sm: 1 }, mb: { xs: 2, sm: 2 }, maxWidth: 600 }}
            >
              Remember to upload proof of banking details under Supporting
              Documentation.
            </Typography>
          </>
        )}

        {refundAccountOption === "Other bank details" && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h5>Account</h5>
            <FormControl
              sx={{
                m: { xs: 0, sm: 1 },
                mb: { xs: 1, sm: 1 },
                width: { xs: 300, sm: 600 },
                minWidth: { xs: "100%", sm: "auto" },
              }}
            >
              <TextField
                id="name"
                name="name"
                label="Name of account holder"
                required
                error={!!formErrors.name}
                helperText={formErrors.name}
                value={formValues.name || ""}
                onChange={(e) =>
                  setFormValues({ ...formValues, name: e.target.value })
                }
              />
            </FormControl>

            <FormControl
              sx={{
                m: { xs: 0, sm: 1 },
                mb: { xs: 1, sm: 1 },
                width: { xs: 300, sm: 600 },
                minWidth: { xs: "100%", sm: "auto" },
              }}
            >
              <TextField
                id="accountNumber"
                name="accountNumber"
                label="Account number"
                required
                error={!!formErrors.accountNumber}
                helperText={formErrors.accountNumber}
                value={formValues.accountNumber || ""}
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    accountNumber: e.target.value,
                  })
                }
              />
            </FormControl>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <h5>Bank</h5>
              <FormControl
                sx={{
                  m: { xs: 0, sm: 1 },
                  mb: { xs: 1, sm: 1 },
                  width: { xs: 300, sm: 600 },
                  minWidth: { xs: "100%", sm: "auto" },
                }}
              >
                <InputLabel htmlFor="bank">Bank</InputLabel>
                <Select
                  id="bank"
                  name="bank"
                  label="Bank"
                  required
                  value={bank}
                  onChange={(e) => {
                    setBank(e.target.value as string);
                    setFormValues({
                      ...formValues,
                      bank: e.target.value as string,
                    });
                  }}
                  error={!!formErrors.bank}
                >
                  <MenuItem value="Standard bank">Standard bank</MenuItem>
                  <MenuItem value="FNB">FNB</MenuItem>
                  <MenuItem value="Nedbank">Nedbank</MenuItem>
                  <MenuItem value="Absa">Absa</MenuItem>
                  <MenuItem value="Capitec">Capitec</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
                {formErrors.bank && (
                  <p style={{ color: "red" }}>{formErrors.bank}</p>
                )}
              </FormControl>
              {bank === "Other" && (
                <FormControl
                  sx={{
                    m: { xs: 0, sm: 1 },
                    mb: { xs: 1, sm: 1 },
                    width: { xs: 300, sm: 600 },
                    minWidth: { xs: "100%", sm: "auto" },
                  }}
                >
                  <TextField
                    id="otherBank"
                    name="otherBank"
                    label="Other bank"
                    required
                    value={formValues.otherBank || ""}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        otherBank: e.target.value,
                      })
                    }
                  />
                </FormControl>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <h5>Account type</h5>
              <FormControl
                sx={{
                  m: { xs: 0, sm: 1 },
                  mb: { xs: 1, sm: 1 },
                  width: { xs: 300, sm: 600 },
                  minWidth: { xs: "100%", sm: "auto" },
                }}
              >
                <InputLabel htmlFor="accountType">Account type</InputLabel>
                <Select
                  id="accountType"
                  name="accountType"
                  label="Account type"
                  required
                  value={accountType}
                  onChange={(e) => {
                    setAccountType(e.target.value as string);
                    setFormValues({
                      ...formValues,
                      accountType: e.target.value as string,
                    });
                  }}
                  error={!!formErrors.accountType}
                >
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Savings">Savings</MenuItem>
                  <MenuItem value="Transmission">Transmission</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
                {formErrors.accountType && (
                  <p style={{ color: "red" }}>{formErrors.accountType}</p>
                )}
              </FormControl>
              {accountType === "Other" && (
                <FormControl
                  sx={{
                    m: { xs: 0, sm: 1 },
                    mb: { xs: 1, sm: 1 },
                    width: { xs: 300, sm: 600 },
                    minWidth: { xs: "100%", sm: "auto" },
                  }}
                >
                  <TextField
                    id="otherAccountType"
                    name="otherAccountType"
                    label="Other account type"
                    required
                    value={formValues.otherAccountType || ""}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        otherAccountType: e.target.value,
                      })
                    }
                  />
                </FormControl>
              )}
            </div>
          </div>
        )}
        <Box sx={{ display: "flex", flexDirection: "row", py: 2, mb: "10em" }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1, fontSize: "1.2em" }}
          >
            Back
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          {isStepOptional(activeStep) && (
            <>
              <Button
                color="inherit"
                onClick={handleOpenModal}
                sx={{ mr: 1, fontSize: "1.2em" }}
              >
                Skip
              </Button>
              <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 400,
                    bgcolor: "background.paper",
                    border: "2px solid #000",
                    boxShadow: 24,
                    p: 4,
                  }}
                >
                  <Typography id="modal-title" variant="h6" component="h2">
                    Kindly note
                  </Typography>
                  <Typography id="modal-description" sx={{ mt: 2 }}>
                    If you do not provide banking details at this point, all
                    valid claims will be paid into the bank account from which
                    we collect your monthly premium.
                  </Typography>
                  <Button
                    onClick={handleCloseModal}
                    sx={{ mt: 2, fontSize: "1.2em" }}
                  >
                    OK
                  </Button>
                </Box>
              </Modal>
            </>
          )}
          <Button type="submit" sx={{ fontSize: "1.2em" }}>
            {activeStep === steps.length - 1 ? "Finish" : "Next"}
          </Button>
        </Box>
      </form>
      <Footer />
    </>
  );
}
